import { useEffect, useState } from 'react'
import { useGameBusValue } from '../hooks/useGameBus'
import SettingsModal from './SettingsModal'
import PauseOverlay from './PauseOverlay'
import { GameBus } from '@game/core/GameBus'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import './HUD.css'



export default function HUD() {
  const { progress } = useAuth()
  const hp = useGameBusValue('hp:changed', { hp: 100, maxHp: 100 })
  const guard = useGameBusValue('guard:changed', { guard: 100, maxGuard: 100 })
  const axe = useGameBusValue('axe:changed', { axe: 0, maxAxe: 100 })
  const combo = useGameBusValue('combo:changed', { count: 0 })
  const corda = useGameBusValue('corda:changed', { corda: 'branca' as const })
  const heal = useGameBusValue('player:healUsed', { charges: 0 })
  const timeInfo = useGameBusValue('time:changed', { elapsedSec: 0 })

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0')
    const s = (secs % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [enemyState, setEnemyState] = useState<{ hp: number; maxHp: number; name: string } | null>(null)
  const navigate = useNavigate()

  // Mostra barra de HP do inimigo atual. Auto-esconde após inatividade
  // (4s sem hit) ou 1.5s depois que o HP zerou.
  useEffect(() => {
    let hideTimer: number | null = null
    const off = GameBus.on('enemy:hp:changed', (data) => {
      setEnemyState(data)
      if (hideTimer) window.clearTimeout(hideTimer)
      const delay = data.hp === 0 ? 1500 : 4000
      hideTimer = window.setTimeout(() => setEnemyState(null), delay)
    })
    return () => {
      off()
      if (hideTimer) window.clearTimeout(hideTimer)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key.toLowerCase() === 'p') {
        if (isSettingsOpen) {
          setIsSettingsOpen(false)
        } else {
          togglePause()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPaused, isSettingsOpen])

  const togglePause = () => {
    const newPaused = !isPaused
    setIsPaused(newPaused)
    GameBus.emit(newPaused ? 'pause:request' : 'resume:request', undefined)
  }

  const isLowHp = hp.hp <= hp.maxHp * 0.3
  const hasAxeFilled = axe.axe >= axe.maxAxe

  const getPlayerPortrait = () => {
    const skinKey = (progress?.skin as string) ?? 'negro'
    if (skinKey === 'raiz' || skinKey === 'negro') return '/assets/ui/skin_negro.png'
    if (skinKey === 'dourado' || skinKey === 'loiro') return '/assets/ui/skin_loiro.png'
    if (skinKey === 'equilibrado') return '/assets/ui/skin_equilibrado.png'
    return '/assets/ui/skin_moreno.png'
  }

  return (
    <div className="hud-container">
      {isLowHp && <div className="vignette-danger" />}
      
      <div className="hud-top-left">
        <div className="hud-player-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <img
            src={getPlayerPortrait()}
            alt="Player Portrait"
            style={{ width: '48px', height: '48px', imageRendering: 'pixelated', marginRight: '8px', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.5)' }}
          />
          <div className="hud-player-name" style={{ margin: 0 }}>PLAYER</div>
        </div>
        <Bar label="HP" value={hp.hp} max={hp.maxHp} color={isLowHp ? '#ff4444' : '#C76C3C'} />
        <Bar label="AXÉ" value={axe.axe} max={axe.maxAxe} color={hasAxeFilled ? '#ffffff' : '#E5B055'} />
        <Bar label="GUARDA" value={Math.floor(guard.guard)} max={guard.maxGuard} color={guard.guard <= 25 ? 'linear-gradient(90deg, #dd2222, #ff4444)' : 'linear-gradient(90deg, #2288ff, #44aaff)'} />
        {heal.charges > 0 && (
          <div className="hud-heal-charges animate-float">
            <img src="/assets/ui/icon_axe.png" alt="Axé" style={{ width: '16px', height: '16px', verticalAlign: 'middle', marginRight: '4px', imageRendering: 'pixelated' }} /> {heal.charges} CARGAS DE AXÉ
          </div>
        )}
      </div>

      <div className="hud-timer">
        {formatTime(timeInfo.elapsedSec)}
      </div>

      {enemyState && (
        <div className="hud-enemy-bar" key={enemyState.name}>
          <div className="hud-enemy-name">COMPUTER / {enemyState.name}</div>
          <div className="hud-bar-row" style={{ width: '100%' }}>
            <span className="hud-bar-label" style={{ color: '#ff8a7a' }}>HP</span>
            <div className="hud-bar-outer hud-enemy-bar-outer">
              <div
                className="hud-bar-inner"
                style={{
                  width: `${Math.max(0, Math.min(100, (enemyState.hp / enemyState.maxHp) * 100))}%`,
                  background: '#C73A3A',
                }}
              />
            </div>
            <span className="hud-bar-value" style={{ color: '#ff8a7a' }}>
              {Math.floor(enemyState.hp)}/{enemyState.maxHp}
            </span>
          </div>
        </div>
      )}

      <div className="hud-top-right">
        <div className="hud-top-right-actions">
          <div className="hud-corda-label">Corda {(corda?.corda || 'branca').toUpperCase()}</div>
          <button 
            className="hud-settings-btn"
            onClick={togglePause}
            aria-label={isPaused ? "Retomar Jogo" : "Pausar Jogo"}
          >
            {isPaused ? '▶' : '||'}
          </button>
        </div>
        {combo.count > 1 && (
          <div key={combo.count} className="hud-combo animate-wobble">
            COMBO ×{combo.count}
          </div>
        )}
      </div>

      <PauseOverlay 
        isOpen={isPaused} 
        onResume={togglePause}
        onExit={() => navigate('/menu')}
        onSettings={() => setIsSettingsOpen(true)}
        skin={progress?.skin}
      />

      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        healCharges={heal.charges}
      />
    </div>
  )
}



function Bar({
  label,
  value,
  max,
  color,
}: {
  label: string
  value: number
  max: number
  color: string
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div className="hud-bar-row">
      <span className="hud-bar-label">{label}</span>
      <div className="hud-bar-outer">
        <div className="hud-bar-inner" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="hud-bar-value">
        {Math.round(value)}/{max}
      </span>
    </div>
  )
}

